import { useEffect, useState } from "react";
import { Star, BadgeCheck, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Stars = ({ n, cls = "w-4 h-4" }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${cls} ${i <= Math.round(n || 0) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
    ))}
  </span>
);

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("it-IT", { month: "long", year: "numeric" }); } catch { return ""; }
};

function HomeReviewForm() {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (submitting) return;
    if (!rating) { toast.error("Seleziona da 1 a 5 stelle."); return; }
    if (name.trim().length < 2) { toast.error("Inserisci il tuo nome."); return; }
    setSubmitting(true);
    try {
      await api.post("/premium-review", { display_name: name.trim(), rating, comment: comment.trim() || null });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invio non riuscito, riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl border border-[#14B8A6]/40 shadow-sm p-6 text-center" data-testid="home-review-done">
        <div className="text-2xl">🙏</div>
        <div className="mt-1 text-sm font-bold text-[#0b2545]">Grazie per la tua recensione!</div>
        <p className="mt-1 text-xs text-slate-500">Sarà pubblicata dopo una rapida verifica del team.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="home-review-form">
      <div className="text-sm font-bold text-[#0b2545]">Lascia la tua recensione</div>
      <p className="mt-1 text-xs text-slate-500">Hai usato il Copilot o ricevuto una valutazione? Raccontalo in 30 secondi.</p>
      <div className="mt-3 flex items-center gap-1" data-testid="home-review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
            className="p-0.5 transition-transform hover:scale-110" data-testid={`home-review-star-${n}`}>
            <Star className={`w-7 h-7 ${n <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Il tuo nome *" maxLength={60}
        className="mt-3 w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50/70 text-sm text-[#0b2545] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/40"
        data-testid="home-review-name" />
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={600}
        placeholder="La tua esperienza (opzionale)"
        className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50/70 text-sm text-[#0b2545] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/40 resize-none"
        data-testid="home-review-comment" />
      <button type="button" onClick={submit} disabled={submitting}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0b2545] text-white text-sm font-semibold hover:bg-[#13315c] disabled:opacity-60"
        data-testid="home-review-submit">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Invia la recensione
      </button>
      <p className="mt-2 text-[10px] text-slate-400">Le recensioni vengono verificate dal team prima della pubblicazione.</p>
    </div>
  );
}

export default function ReviewsSection() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/premium-reviews").then(({ data: d }) => setData(d)).catch(() => {});
  }, []);

  const count = data?.count || 0;

  return (
    <section id="recensioni" className="py-24 lg:py-28 bg-[#F0F9FC]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="enea-eyebrow flex items-center gap-3 mb-5 text-[#14B8A6]">
          <Star className="w-4 h-4" />
          <span className="text-[#2c7da0]">08 · Recensioni</span>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="text-3xl lg:text-4xl font-display font-semibold text-[#0b2545]">
            Cosa dicono i nostri <span className="text-[#0d9488]">clienti</span>
          </h2>
          {count > 0 && (
            <div className="flex items-center gap-2.5" data-testid="reviews-avg">
              <Stars n={data.avg} cls="w-5 h-5" />
              <span className="text-lg font-bold text-[#0b2545]">{String(data.avg).replace(".", ",")}/5</span>
              <span className="text-sm text-slate-500">· {count} recension{count === 1 ? "e" : "i"}</span>
            </div>
          )}
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {(data?.reviews || []).slice(0, 5).map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 flex flex-col" data-testid="review-card">
              <div className="flex items-center justify-between">
                <Stars n={r.rating} />
                <span className="text-[10px] text-slate-400">{fmtDate(r.timestamp)}</span>
              </div>
              {r.comment && <p className="mt-3 text-sm text-slate-700 leading-relaxed flex-1">“{r.comment}”</p>}
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0b2545] truncate">{r.name}</div>
                  {r.comune && <div className="text-[11px] text-slate-500">{r.comune}</div>}
                </div>
                {r.verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0d9488] bg-[#14B8A6]/10 rounded-full px-2 py-1 flex-shrink-0">
                    <BadgeCheck className="w-3 h-3" /> Premium verificato
                  </span>
                )}
              </div>
            </div>
          ))}
          <HomeReviewForm />
        </div>
        <p className="mt-6 text-[11px] text-slate-500">Le recensioni con badge “Premium verificato” provengono esclusivamente da clienti che hanno ricevuto la brochure Valutazione Premium ENEA Specialist.</p>
      </div>
    </section>
  );
}
